import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Download, CheckCircle2, Clock, ShieldCheck, PenTool,
  Upload, X, RefreshCw, AlertCircle, FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { downloadDocumentPdf } from '../utils/downloadPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ESSAppointmentOrder() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [blockedStatus, setBlockedStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Signature Modal State
  const [showSignModal, setShowSignModal] = useState(false);
  const [sigType, setSigType] = useState('mouse'); // 'mouse' | 'upload'
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedSigB64, setUploadedSigB64] = useState('');

  // Signature Canvas Ref
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  const fetchMyDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: docs }, { data: bStatus }] = await Promise.all([
        axios.get(`${API_URL}/documents/issued`, { withCredentials: true }),
        axios.get(`${API_URL}/documents/blocked-status`, { withCredentials: true }).catch(() => ({ data: { is_blocked: false } }))
      ]);
      setDocuments(docs || []);
      setBlockedStatus(bStatus || null);
      if (docs && docs.length > 0 && !selectedDoc) {
        setSelectedDoc(docs[0]);
      }
    } catch (e) {
      toast.error('Failed to load your official letters');
    }
    setLoading(false);
  }, [selectedDoc]);

  useEffect(() => {
    fetchMyDocuments();
  }, [fetchMyDocuments]);

  // Canvas Mouse Drawing Helpers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0F172A';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedSigB64(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitSignature = async () => {
    if (!selectedDoc) return;
    let sigImage = '';

    if (sigType === 'mouse') {
      const canvas = canvasRef.current;
      if (canvas) {
        sigImage = canvas.toDataURL('image/png');
      }
    } else {
      sigImage = uploadedSigB64;
    }

    if (!sigImage) {
      toast.error('Please draw or upload your signature before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const docId = selectedDoc.id || selectedDoc._id;
      const { data } = await axios.post(`${API_URL}/documents/issued/${docId}/acknowledge`, {
        signature_type: sigType,
        signature_image: sigImage,
        accepted_terms: acceptedTerms,
      }, { withCredentials: true });

      toast.success('Official letter acknowledged & signed! Portal access restored.');
      setShowSignModal(false);
      fetchMyDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Acknowledgement failed');
    }
    setSubmitting(false);
  };

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="ess-appointment-order-page">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileCheck className="w-7 h-7 text-[#0052FF]" /> My Official Letters
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            View, download, and digitally sign your Official Letters, Offer Letter, and employment contracts.
          </p>
        </div>

        {/* Global Blocked Top Alert Banner */}
        {blockedStatus?.is_blocked && (
          <div className="bg-red-500 text-white p-4 rounded-2xl shadow-lg flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 shrink-0 animate-pulse text-amber-300" />
              <div>
                <h4 className="font-bold text-base">⚠️ Portal Access Blocked (72h Overdue)</h4>
                <p className="text-xs text-red-100 mt-0.5">
                  Your employee portal access is currently restricted. Please complete your digital signature below to restore full access immediately.
                </p>
              </div>
            </div>
            {selectedDoc && selectedDoc.status !== 'acknowledged' && (
              <Button
                onClick={() => setShowSignModal(true)}
                className="bg-white text-red-700 hover:bg-red-50 font-bold text-xs px-4 py-2 rounded-xl shrink-0"
              >
                Sign Now &amp; Restore Access
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading your documents...
          </div>
        ) : documents.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-700">No Official Letters Found</h3>
            <p className="text-xs text-slate-400 mt-1">
              Your HR department has not issued an official letter yet. Once issued, it will appear here for your digital signature.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Sidebar: Documents List */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-800 text-sm">Issued Letters ({documents.length})</h3>
              <div className="space-y-2">
                {documents.map(doc => {
                  const docId = doc.id || doc._id;
                  const isSelected = selectedDoc && (selectedDoc.id === docId || selectedDoc._id === docId);
                  const isAck = doc.status === 'acknowledged';
                  const isBlockedDoc = doc.portal_access_status === 'blocked';
                  const warnLvl = doc.warning_level || 0;

                  return (
                    <button
                      key={docId}
                      onClick={() => setSelectedDoc(doc)}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        isSelected
                          ? 'bg-blue-50/70 border-blue-500 shadow-sm'
                          : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-900 text-sm capitalize">
                          {(doc.doc_type || '').replace(/_/g, ' ')}
                        </span>
                        {isAck ? (
                          <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Signed
                          </span>
                        ) : isBlockedDoc ? (
                          <span className="text-[10px] font-bold bg-red-100 text-red-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <AlertCircle className="w-3 h-3 text-red-600" /> Blocked
                          </span>
                        ) : warnLvl > 0 ? (
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3 text-amber-600" /> Warn L{warnLvl}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3 text-blue-600" /> Action Required
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-slate-500 mt-2 flex items-center justify-between">
                        <span>Issued: <b>{doc.issued_at ? doc.issued_at.slice(0, 10) : '—'}</b></span>
                        <span className="font-mono text-[10px]">v{doc.template_version || 1}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Panel: Selected Document Viewer & Digital Signature Bar */}
            {selectedDoc && (
              <div className="md:col-span-2 space-y-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 pb-4 border-b border-slate-100">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 capitalize">
                        {(selectedDoc.doc_type || '').replace(/_/g, ' ')}
                      </h2>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">
                        Ref: APPT/{selectedDoc.employee_code}/{new Date().getFullYear()} · Issued on {selectedDoc.issued_at?.slice(0, 10)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => downloadDocumentPdf(selectedDoc.id || selectedDoc._id, `${selectedDoc.employee_code || 'EMP'}_Appointment_Order.pdf`)}
                        className="px-3 py-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-xl font-semibold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </button>

                      {selectedDoc.status !== 'acknowledged' && (
                        <Button
                          onClick={() => setShowSignModal(true)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-xs"
                          data-testid="ess-sign-btn"
                        >
                          <PenTool className="w-3.5 h-3.5 mr-1.5" /> Sign &amp; Acknowledge
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Status & Warning Banner */}
                  {selectedDoc.status === 'acknowledged' ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 text-xs text-emerald-900 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold">Digitally Acknowledged &amp; Signed</div>
                        <div className="mt-0.5 opacity-90">
                          Signed on <b>{selectedDoc.acknowledged_at ? new Date(selectedDoc.acknowledged_at).toLocaleString() : '—'}</b> from IP Address: <b>{selectedDoc.acknowledgement_ip || 'Captured'}</b>.
                        </div>
                      </div>
                    </div>
                  ) : selectedDoc.portal_access_status === 'blocked' ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 text-xs text-red-900 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold text-red-900">🚨 Access Restricted (Day 3 Overdue)</div>
                        <div className="mt-0.5 opacity-90 text-red-800">
                          Your official letter acknowledgement is overdue by 72 hours. Your employee portal features have been temporarily locked. Sign below to immediately unblock your portal.
                        </div>
                      </div>
                    </div>
                  ) : selectedDoc.warning_level === 2 ? (
                    <div className="bg-amber-50 border border-amber-300 rounded-xl p-3.5 text-xs text-amber-900 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold text-amber-900">⚠️ Final Warning 2 (48h Overdue)</div>
                        <div className="mt-0.5 opacity-90 text-amber-800">
                          Please acknowledge this document promptly. If unacknowledged within 24 hours, your employee portal access will be automatically blocked.
                        </div>
                      </div>
                    </div>
                  ) : selectedDoc.warning_level === 1 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 flex items-start gap-3">
                      <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold text-amber-900">⚠️ Warning 1 (24h Overdue)</div>
                        <div className="mt-0.5 opacity-90 text-amber-800">
                          Your official letter has not been acknowledged. Please review and digitally sign your document.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-xs text-blue-900 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold">Digital Signature Required</div>
                        <div className="mt-0.5 opacity-90">
                          Please review your letter terms and click <b>"Sign &amp; Acknowledge"</b> to submit your digital mouse signature.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Embedded PDF Viewer Stream */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden h-[550px] bg-slate-100">
                    <iframe
                      src={`${API_URL}/documents/issued/${selectedDoc.id || selectedDoc._id}/pdf?disposition=inline`}
                      title="Official Letter PDF"
                      className="w-full h-full border-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DIGITAL SIGNATURE MODAL */}
        {showSignModal && selectedDoc && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-[#0052FF]" /> Digital Signature &amp; Acknowledgement
                </h3>
                <button onClick={() => setShowSignModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                <div className="flex items-center justify-center gap-3 bg-slate-100 p-1 rounded-xl">
                  <button
                    onClick={() => setSigType('mouse')}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      sigType === 'mouse' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    Draw Signature (Mouse/Touch)
                  </button>
                  <button
                    onClick={() => setSigType('upload')}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      sigType === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    Upload Signature Image
                  </button>
                </div>

                {sigType === 'mouse' ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="font-bold text-slate-700">Draw Signature inside box below:</label>
                      <button type="button" onClick={clearCanvas} className="text-blue-600 hover:underline font-semibold">
                        Clear
                      </button>
                    </div>
                    <canvas
                      ref={canvasRef}
                      width={440}
                      height={140}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl cursor-crosshair touch-none"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Upload Signature Image (PNG / JPG)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                    />
                    {uploadedSigB64 && (
                      <div className="mt-2 text-center p-3 border border-slate-200 rounded-xl bg-slate-50 flex items-center justify-center">
                        <img src={uploadedSigB64} alt="Signature Preview" className="max-h-20 max-w-[220px] object-contain mx-auto border border-dashed border-slate-300 p-1 bg-white rounded-lg shadow-2xs" />
                      </div>
                    )}
                  </div>
                )}

                <label className="flex items-start gap-2 cursor-pointer font-medium text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={e => setAcceptedTerms(e.target.checked)}
                    className="w-4 h-4 rounded text-blue-600 mt-0.5"
                  />
                  <span>
                    I confirm that I have carefully read and accepted all terms, conditions, and salary annexure outlined in this official letter.
                  </span>
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowSignModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleSubmitSignature}
                    disabled={submitting || !acceptedTerms}
                    className="bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold"
                  >
                    {submitting ? 'Submitting...' : 'Sign & Complete Acknowledgement'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
